import { useState } from "react";
import { Copy, Facebook, Linkedin, Mail, MessageCircle, Share2, Twitter } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export const shareUrl = (p) => `${API}/share/${p.slug}`;

export const copyLink = async (p) => {
  try {
    await navigator.clipboard.writeText(shareUrl(p));
    toast.success("Link copied. It shows a preview card when you paste it.");
  } catch {
    toast.error("Couldn’t copy the link. Copy it from the address bar instead.");
  }
};

export const nativeShare = async (p) => {
  if (!navigator.share) return copyLink(p);
  try {
    await navigator.share({ title: p.title, text: `${p.title} on ReimagineCork`, url: shareUrl(p) });
  } catch (err) {
    if (err?.name === "AbortError") return;
    console.warn("Share sheet failed; copying the link instead", err);
    await copyLink(p);
  }
};

const targets = (p) => {
  const url = encodeURIComponent(shareUrl(p));
  const text = encodeURIComponent(`${p.title} on ReimagineCork`);
  return [
    ["whatsapp", "WhatsApp", MessageCircle, `https://wa.me/?text=${text}%20${url}`],
    ["facebook", "Facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${url}`],
    ["x", "X", Twitter, `https://twitter.com/intent/tweet?text=${text}&url=${url}`],
    ["linkedin", "LinkedIn", Linkedin, `https://www.linkedin.com/sharing/share-offsite/?url=${url}`],
    ["email", "Email", Mail, `mailto:?subject=${text}&body=${text}%0A${url}`],
  ];
};

export const SharePopover = ({ p, className = "btn btn-ghost btn-block" }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={className} data-testid="share-btn"><Share2 size={17} />Share</button>
      </PopoverTrigger>
      <PopoverContent align="end" side="top" style={{ width: 340, padding: 16, borderRadius: 16 }} data-testid="share-popover">
        <p className="head" style={{ fontSize: 18, fontWeight: 700, margin: "0 0 10px" }}>Share this proposal</p>
        <img src={`${shareUrl(p)}/card.jpg?v=${p.likes}`} alt="Preview of the card people see when you share this link" data-testid="share-card-preview"
          style={{ width: "100%", aspectRatio: "1200 / 630", borderRadius: 10, border: "1px solid var(--border)", display: "block", background: "#f6f9fa" }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6, margin: "14px 0 12px" }}>
          {targets(p).map(([id, label, Icon, href]) => (
            <a key={id} href={href} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid={`share-${id}`}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "8px 2px", borderRadius: 10, textDecoration: "none", color: "var(--ink-2)", fontSize: 12, fontWeight: 600 }}
              className="share-target">
              <span style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--tint)", color: "var(--red-dark)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={18} /></span>
              {label}
            </a>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => { copyLink(p); setOpen(false); }} data-testid="share-copy-btn">
            <Copy size={16} />Copy link
          </button>
          {typeof navigator !== "undefined" && navigator.share && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { nativeShare(p); setOpen(false); }} data-testid="share-more-btn">More…</button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
